<template>
    <section class="page-faq m-b-36 m-t-12 m-t-small-0">
        <h1 class="d-none">
            Peekage - FAQ | Digital Product Sampling Platform
        </h1>
        <container size="lg" class="p-x-large-0">
            <div
                class="bg-image cover faq-page-header m-b-100 m-b-small-50 p-t-66 p-b-84 p-x-200 p-y-small-40 p-x-small-20 p-y-large-50 p-x-large-20 rounded"
                :style="{ backgroundImage: `url(${$image('arts/faq.png')})`}"
            >
                <Texture
                    type="display"
                    size="md"
                    tag="h2"
                    color="on-color"
                    class="f-w-700 m-b-42 text-center"
                >
                    Answers from the Peekage Team
                </Texture>

                <div class="bg-color on-color faq-search d-flex align-items-center position-relative">
                    <span class="icon bg-color primary-inverse search m-x-20" />
                    <input v-model="keyword"
                           type="text"
                           placeholder="Search for answers"
                           class="input-search color w-100 primary p-r-20 p-y-18 font f-s-18 l-h-28 f-w-400 border-0"
                    >
                    <span v-if="isSearching" class="only-desktop text sm font text-right m-r-20 search-counter"><span class="font f-manrop-bold">{{ articles.length }}</span> <span class="color secondary">answers found</span></span>
                </div>
            </div>
        </container>

        <container size="lg">
            <div class="m-x-200 m-l-small-28 m-r-small-28 m-l-large-28 m-r-large-28" v-if="articles && articles.length">
                <template
                    v-for="topic in topics"
                >
                    <div class="ml-16"
                         :class="{ 'p-y-38 border-bottom' : !isSearching }"
                         :key="topic._id"
                         v-if="filterById(topic._id).length"
                    >
                        <div class="d-flex align-items-center m-b-16" v-if="!isSearching">
                            <span :class="`icon icon-lg m-r-32 accent-1st ${topic.icon}`" />
                            <Heading
                                style="letter-spacing: -0.75px;"
                                size="x-sm"
                                tag="h3"
                                color="accent-gradiant"
                                class="f-w-600 m-b-0"
                            >
                                {{ topic.name }}
                            </Heading>
                        </div>

                        <template v-if="!isSearching">
                            <template v-if="topic._id === '66e82fde9de515001ce1812b'">
                                <div class="m-l-104 m-y-28 m-l-small-0" style="overflow: hidden; border-radius: 8px;">
                                    <iframe style="min-height: 350px" width="100%" height="100%" src="https://www.youtube.com/embed/5KtjauERLZM?si=X183YzM1Eiemc4Rj" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen />
                                </div>
                            </template>
                        </template>

                        <div class="accordion">
                            <div
                                :class="{ 'm-l-64 m-b-16' : !isSearching , 'border-bottom p-y-24': isSearching }"
                                class="m-l-small-5 "
                                v-for="article in filterById(topic._id) "
                                :key="article._id"
                                :id="`article-${article._id}`"
                            >
                                <div class="accordion-item d-flex" v-b-toggle="`collapse-${article._id}`">
                                    <span class="accordion-item-icon icon size sm chevron-right m-t-2 m-r-18 color primary" />
                                    <Texture
                                        size="lg"
                                        tag="h4"
                                        color="primary"
                                        class="f-w-700 m-b-6"
                                    >
                                        {{ article.title }}
                                    </Texture>
                                </div>
                                <b-collapse :id="`collapse-${article._id}`" :visible="article._id == id" :style="{
                                    display: article._id == id ? 'block !important' : 'none !important'
                                }"
                                >
                                    <div class="m-l-42 m-l-small-0">
                                        <Texture
                                            size="md"
                                            tag="pre"
                                            color="secondary"
                                            style="white-space: break-spaces;"
                                            class="f-w-400 m-b-0"
                                            v-html="article.content.trim()"
                                        />
                                    </div>
                                </b-collapse>
                            </div>
                        </div>
                    </div>
                </template>
            </div>

            <div class="m-t-36 m-b-112">
                <div class="d-flex justify-content-center">
                    <center>
                        <span class="icon chat size md accent-1st m-b-18" />
                        <h5 class="heading x-sm font f-w-600 m-b-8 color primary">
                            Couldn’t find your answer?
                        </h5>
                        <p class="text sm font f-w-400 color secondary m-b-24">
                            Don’t worry, our support team will help you
                        </p>

                        <tuber
                            class="btn md secondary f-s-16 l-h-24 f-w-700 "
                            text="Contact us"
                            v-zendesk-widget
                        />
                    </center>
                </div>
            </div>
        </container>
        <zendesk-widget />
    </section>
</template>

<script>
import get from 'lodash/get';
import ZendeskWidget from '~/components/common/zendesk/widget';
import { breadcrumb, webPageSchema } from '~/scripts/utils/constants/structured-data/index';

const iconMapper = [
    {
        id: ['5f3d22aa22eb40001c3aaa51', '5f3d279822eb40001c3aaa62', '5f3d274722eb40001c3aaa5f'],
        icon: 'badge-icon badge-icon-hint',
    },
    {
        id: ['5f3d294622eb40001c3aaa65', '5f747705afc7d6001ee8f433'],
        icon: 'packs-outline',
    },
    {
        id: ['5f3e427722eb40001c3aaa71', '5f74771eafc7d6001ee8f435'],
        icon: 'privacy-alt',
    },
    {
        id: ['5f3d4bda22eb40001c3aaa6c', '5f74770eafc7d6001ee8f434'],
        icon: 'badge-icon badge-icon-shipment',
    },
    {
        id: ['5f3e454f22eb40001c3aaa76', '5f747adeafc7d6001ee8f451'],
        icon: 'profile profile-outline',
    },
];

export default {
    components: {
        ZendeskWidget,
    },
    layout: 'xerneas',
    async asyncData(context) {
        try {
            const faqTopics = await context.app.$axios.$get('v1/faq/topics');
            const faq = await context.app.$axios.$get('v1/faq');

            const topics = faqTopics.map((topic) => {
                const id = topic._id;
                const mapper = iconMapper.find((el) => {
                    return el.id.includes(id);
                });

                topic = Object.assign({}, topic, {
                    icon: get(mapper, 'icon', 'badge-icon badge-icon-hint'),
                });

                return topic;
            });

            return {
                articles: faq,
                defaultArticles: faq,
                topics,
                structuredData: {
                    '@context': 'https://schema.org',
                    '@type': 'FAQPage',
                    mainEntity: faq.map(el => {
                        return {
                            '@type': 'Question',
                            name: el.title,
                            acceptedAnswer: {
                                '@type': 'Answer',
                                text: el.content.replace(/(\r\n|\n|\r)/gm, ''),
                            },
                        };
                    }),
                },
            };
        } catch (error) {
            return context.error({ statusCode: 500, message: error.message });
        }
    },
    data() {
        return {
            keyword: '',
            articles: [],
            defaultArticles: [],
            topics: [],
            structuredData: {},
        };
    },
    head() {
        return {
            title: 'Frequently Asked Questions | Peekage: Product Sampling Platform',
            __dangerouslyDisableSanitizers: ['script'],
            script: [
                { innerHTML: JSON.stringify(this.structuredData), type: 'application/ld+json' },
                {
                    innerHTML: JSON.stringify(breadcrumb({
                        name: 'FAQ',
                        uri: this.$route.path,
                    })),
                    type: 'application/ld+json',
                },
                { innerHTML: JSON.stringify(webPageSchema), type: 'application/ld+json' },
            ],
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: 'Find answers to all frequently asked questions and learn how to get free samples and gift cards through the Peekage digital product sampling platform.',
                },
            ],
            // script: [
            //     {
            //         id: 'ze-snippet',
            //         hid: 'stripe',
            //         defer: true,
            //         src: 'https://static.zdassets.com/ekr/snippet.js?key=9cb297c6-ac1a-4f2a-8005-7f187ae90057',
            //     },
            // ],
            link: [
                {
                    rel: 'canonical',
                    href: 'https://peekage.com/faq',
                },
            ],
        };
    },
    computed: {
        isSearching() {
            return this.keyword.length;
        },
        id() {
            return get(this, '$route.hash', '').replace('#', '');
        },
    },
    watch: {
        keyword(value) {
            const pattern = new RegExp(value, 'ig');

            const filteredData = this.defaultArticles.filter((article) => {
                return article.content.match(pattern) || article.title.match(pattern);
            });

            this.articles = filteredData;
        },

    },
    mounted() {
        this.$nextTick(() => {
            try {
                if (this.id) {
                    this.$scrollTo(`#article-${this.id}`, 1000, { offset: -100 });
                }

                const zendesk = document.getElementById('launcher');
                const zendeskForm = document.getElementById('webWidget');
                zendesk.style.display = 'block';
                zendeskForm.style.display = 'block';
            } catch (error) {}
        });
    },
    beforeDestroy() {
        try {
            const zendesk = document.getElementById('launcher');
            const zendeskForm = document.getElementById('webWidget');
            zendesk.style.display = 'none';
            zendeskForm.style.display = 'none';
        } catch (error) {}
    },
    methods: {
        filterById(topicId) {
            return this.articles.filter((article) => {
                const topicIds = article.topics.map(topic => topic._id);
                return topicIds.includes(topicId);
            });
        },
        triggerDefault() {
            try {
                const zendesk = document.getElementById('launcher');
                const btn = zendesk.contentWindow.document.querySelector('button');
                btn.click();
            } catch (error) {

            }
        },
    },
};
</script>

<style lang="scss">
.page-faq {
    .faq-search {
        border-radius: 8px;
        overflow: hidden;
        .input-search {
            &:focus {
                outline: unset;
            }
        }

        .search-counter {
            width: 40%;
            position: absolute;
            right: 0px;
        }
    }
}
.accordion {
    .accordion-item {
        .accordion-item-icon {
            transition: all .2s
        }
        &:not(.collapsed) {
            .accordion-item-icon {
                transform: rotate(90deg)
            }
        }
    }
}
</style>
